/**
 * The module responsible for managing localization within the application
 */
import OptionCollection from 'app/src/data/models/OptionCollection';
import LocaleCollection from 'app/src/data/models/LocaleCollection';
import CurrencyCollection from 'app/src/data/models/CurrencyCollection';
import CurrencyDenominationCollection from 'app/src/data/models/CurrencyDenominationCollection';
import Format from 'app/src/data/Format';
import Modules from 'shared_modules/Modules';
import $ from 'jquery';

let _locale = null;
let _locales = null;
let _currency = null;
let _currencies = null;
let _options = null;

const DEFAULT_LOCALE = 'United States';
const DEFAULT_CURRENCY = 'USD';

export default class Localize {
  /**
   * Initialize the localization module with the given locale and resource file
   *
   * @returns {jQuery.Promise}  a promise that resolves when the module has finished initializing
   * Fails otherwise.
   */
  static init() {
    let options = new OptionCollection();
    let locales = new LocaleCollection();
    let currencies = new CurrencyCollection();

    _locale = null;
    _locales = null;
    _currency = null;
    _currencies = null;
    _options = null;

    return $.when(options.fetch(), locales.fetch(), currencies.fetch())
      .done(() => {
        const localeOption = options.findWhere({ name: 'locale' });
        const defaultLocale = locales.findWhere({ name: DEFAULT_LOCALE });
        let selectedLocale = null;

        if (localeOption) {
          selectedLocale = locales.findWhere({ name: localeOption.get('value') });
        }

        _locale = selectedLocale || defaultLocale;
        _locales = locales;

        const currencyOption = options.findWhere({ name: 'currency_code' });
        const defaultCurrency = currencies.findWhere({
          code: _locale ? _locale.attributes.currencyCode : DEFAULT_CURRENCY,
        });
        let selectedCurrency = null;

        if (currencyOption) {
          selectedCurrency = currencies.findWhere({ code: currencyOption.get('value') });
        }

        _currency = selectedCurrency || defaultCurrency;
        _currencies = currencies;

        _options = options;

        window.Handlebars.registerHelper('translate', translateHelper);
      })
      .fail(() => {
        console.error('Failed to initialize the localization module.');
      });
  }

  /**
   * Retrieve the localized string given the string identifier
   * @param key {String}      The key of the desired string for translation
   * @param options {Object}  A hash of options to use with the translation request
   *
   * @returns {string} the localized string
   */
  static translate(key, options = {}) {
    // TODO: select a translation library and use its translation function here
    return key;
  }

  /**
   * Retrieve the localized denominations for the currently selected locale.
   *
   * @returns {CurrencyDenominationCollection}
   */
  static getLocalizedDenominations() {
    let denominations = null;

    if (Modules.hasModule('currency_option') && _currency) {
      denominations = _currency.getRelation(
        'CurrencyDenominations',
        CurrencyDenominationCollection
      );
      denominations = new CurrencyDenominationCollection(
        denominations.filter(denomination => Format.parseBoolean(denomination.get('significant')))
      );
    } else if (_locale) {
      denominations = _locale.getRelation('CurrencyDenominations', CurrencyDenominationCollection);

      let filteredDenominations = denominations.filter(
        denomination => denomination.get('value') >= 1
      );
      filteredDenominations = filteredDenominations.reverse();
      denominations.reset(filteredDenominations);
    }

    return denominations;
  }

  /**
   * Retrieves the currency symbol for the selected locale/currency selection
   *
   * @returns {string}    The currency symbol
   */
  static getCurrencySymbol() {
    let currencySymbol = '';

    if (Modules.hasModule('currency_option') && _currency) {
      currencySymbol = _currency.get('symbol');
    } else if (_locale) {
      currencySymbol = _locale.get('currencySymbol');
    }

    return currencySymbol;
  }

  /**
   * Retrieves the currency precision from the selected locale/currency selection
   */
  static getCurrencyPrecision() {
    let currencyPrecision = '';

    if (Modules.hasModule('currency_option') && _currency) {
      currencyPrecision = _currency.get('decimalPrecision');
    } else if (_locale) {
      currencyPrecision = _locale.get('currencyPrecision');
    }

    // We need to parseInt the value since sometimes this is returned
    // as a String. This cause an issue with the BigNumber.toFixed() method
    // which is expecting the primitive Number.
    return parseInt(currencyPrecision, 0);
  }

  static get locale() {
    return _locale;
  }

  /**
   * Retrieves Options
   */
  static get options() {
    return _options;
  }

  /**
   * Retrieves Options
   */
  static set options(o) {
    _options = o;
  }

  /**
   * Change the current locale to the specified locale
   *
   * @param locale {string}   The desired locale
   */
  static changeLocale(locale) {
    if (locale && _locales) {
      let newLocale = _locales.findWhere({ name: locale });
      if (newLocale) {
        _locale = newLocale;

        // TODO: change languages via the translation library and trigger a re-render event
      }
    }
  }
}

// Handlebars template helper that will be called to translate a string from within a Handlebars
// template.
function translateHelper(key, options) {
  var result = Localize.translate(key, options);

  return new window.Handlebars.SafeString(result);
}
